import { useWindowSize } from "@react-hookz/web";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Drawer, DropdownSelect, Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";
import { Tab, TabProps } from "./Tab";

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs?: TabProps[];
  /**
   * How tabs render below the lg (1024px) breakpoint: a compact DropdownSelect (default),
   * or an input-styled field that opens a "Select Tab" bottom sheet (client portal mobile design).
   */
  mobileVariant?: "select" | "sheet";
}

export const Tabs = ({ children, className, tabs, mobileVariant = "select", ...props }: TabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const current = pathSegments.filter(Boolean).pop();

  const { width } = useWindowSize();
  const [selected, setSelected] = useState<string | undefined>(current);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const onChange = (nav: string) => {
    setSelected(nav);
    navigate(nav);
  };

  const options = useMemo(() => {
    return tabs?.map(({ name, to }) => ({ name, value: to }));
  }, [tabs]);

  if (tabs && tabs.length > 0 && width < 1024 && mobileVariant === "sheet") {
    const activeName = tabs.find((tab) => tab.to === current)?.name ?? tabs[0].name;
    const closeSheet = () => setIsSheetOpen(false);

    return (
      <Drawer open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-lg border border-neutral-mid-gray bg-white px-3 py-2 text-left"
        >
          <Typography variant="paragraph2" className="text-neutral-black">
            {activeName}
          </Typography>
          <Icon name="arrow-down" isCustom className="h-5 w-5 shrink-0 text-black" />
        </button>

        <div className="flex flex-col gap-3 px-1 pb-4 pt-2">
          <Typography variant="title" className="mb-2">
            Select Tab
          </Typography>
          {tabs.map(({ name, to }) => (
            <button
              key={to}
              type="button"
              className="rounded-lg bg-neutral-surface-gray px-6 py-3 text-left"
              onClick={() => {
                navigate(to);
                closeSheet();
              }}
            >
              <Typography variant="paragraph2" className="font-semibold text-neutral-dark-gray">
                {name}
              </Typography>
            </button>
          ))}
          <Button variant="tertiary" size="md" className="mt-2 w-full" onClick={closeSheet}>
            Close
          </Button>
        </div>
      </Drawer>
    );
  }

  if (options && width < 1024)
    return (
      <DropdownSelect
        size="sm"
        options={options}
        onChange={onChange}
        value={selected}
        className={clsx("w-full max-w-[320px] border-none", className)}
      />
    );

  return (
    <nav className={clsx("flex max-w-full overflow-auto border-b border-transparent", className)} aria-label="Tabs" {...props}>
      {tabs ? tabs.map((tab, i) => <Tab key={i} {...tab} />) : children}
    </nav>
  );
};

Tabs.Tab = Tab;
